import {FilterInterface} from "../../interfaces/catalog/FilterInterface";
import {useEffect, useState} from "react";
import Range, {RANGE_DELIMITER} from "../core/Range";
import {RangeFilterInterface} from "../../interfaces/core/RangeFilterInterface";
import {CatalogFilterInterface} from "../../interfaces/catalog/CatalogFilterInterface";
import {getSuffixByCount} from "../../utils/Utils";
import CheckboxFilterGroup from "./CheckboxFilterGroup";
import {useLocation, useSearchParams} from "react-router-dom";

const PROPERTY_SEPARATOR = '~'
const PROPERTY_VALUE_SEPARATOR = '|'

export default function Filters({priceFilter, filters, brands, toggleFilters, setFilters, productCount}: {
    priceFilter: RangeFilterInterface,
    filters: FilterInterface[],
    brands: string[],
    toggleFilters: () => void,
    setFilters: (filters: CatalogFilterInterface) => void,
    productCount: number
}) {

    const [priceFilterLocal, setPriceFilterLocal] = useState<RangeFilterInterface>(priceFilter);
    const [filterLocal, setFilterLocal] = useState<Record<number, string[]>>({});
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSortOrder, setShowSortOrder] = useState(false);


    useEffect(() => {
        setIsInitialized(false);
        const filtersParam = searchParams.get('filters');

        if (filtersParam) {
            // Парсим параметр filters
            const parsedFilters: CatalogFilterInterface = {
                price: {min: 0, max: 0},
                filters: {},
                brands: []
            };

            const filterParts = filtersParam.split(PROPERTY_SEPARATOR);

            filterParts.forEach(part => {
                if (part.startsWith('b:')) {
                    // Бренды: b:brand1|brand2
                    parsedFilters.brands = part.substring(2).split(PROPERTY_VALUE_SEPARATOR);
                } else if (part.startsWith('p:')) {
                    // Цена: p:min-max
                    const priceRange = part.substring(2).split('-');
                    parsedFilters.price.min = parseInt(priceRange[0]) || 0;
                    parsedFilters.price.max = parseInt(priceRange[1]) || 0;
                } else if (part.match(/^f\d+:/)) {
                    // Фильтры: f123:value1|value2
                    const match = part.match(/^f(\d+):(.+)$/);
                    if (match) {
                        const filterId = parseInt(match[1]);
                        parsedFilters.filters[filterId] = match[2].split(PROPERTY_VALUE_SEPARATOR);
                    }
                }
            });
            if(Object.values(parsedFilters.filters).length > 0){
                setFilterLocal(parsedFilters.filters)
            }

            if(parsedFilters.brands.length > 0){
                setSelectedBrands(parsedFilters.brands);
            }

            if(parsedFilters.price.max > 0){
                setPriceFilterLocal(parsedFilters.price);
            }

        } else {
            setFilterLocal({})
            setSelectedBrands([])
        }

        setTimeout(() => setIsInitialized(true), 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        // Не обновляем URL при первичной инициализации фильтров из URL
        if (!isInitialized) {
            return;
        }

        const str = [];

        if(selectedBrands.length > 0){
            str.push(`b:${selectedBrands.join(PROPERTY_VALUE_SEPARATOR)}`)
        }
        if(priceFilterLocal.min > priceFilter.min || priceFilterLocal.max < priceFilter.max){
            str.push(`p:${priceFilterLocal.min}-${priceFilterLocal.max}`)
        }
        Object.keys(filterLocal).forEach(filterId => {
            const values = filterLocal[parseInt(filterId)];
            if(values.length > 0){
                str.push(`f${filterId}:${values.join(PROPERTY_VALUE_SEPARATOR)}`)
            }
        })

        if(str.length > 0) {
            setSearchParams({filters: str.join(PROPERTY_SEPARATOR)}, { replace: true, preventScrollReset: true });
        } else {
            setSearchParams({}, { replace: true, preventScrollReset: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBrands, priceFilterLocal, filterLocal , isInitialized]);

    useEffect(() => {
        if(!priceFilterLocal.max && priceFilter.max){
            setPriceFilterLocal(priceFilter);
        }
    }, [priceFilter]);

    useEffect(() => {
        setFilters({
            price: priceFilterLocal,
            filters: filterLocal,
            brands: selectedBrands
        });
    }, [priceFilterLocal, filterLocal, selectedBrands]);

    const toggleLocalFilter = (filterId: number, value: string) => {
        if(filterLocal[filterId] && filterLocal[filterId].includes(value)){
            setFilterLocal({
                ...filterLocal,
                [filterId]: filterLocal[filterId].filter(v => v !== value)
            });
        }else{
            setFilterLocal({
                ...filterLocal,
                [filterId]: filterLocal[filterId] ? [...filterLocal[filterId], value] : [value]
            });
        }
    }

    const setRangeFilter = (filterId: number, value: RangeFilterInterface) => {
        if(value.min === 0 && value.max === 0){
            setFilterLocal({
                ...filterLocal,
                [filterId]: []
            });
        }else{
            setFilterLocal({
                ...filterLocal,
                [filterId]: [value.min + RANGE_DELIMITER + value.max]
            });
        }
    }

    const isAllValueNumeric = (values: string[] ) => {
        return values.filter(value => !/^[\d\.]+$/.test(value)).length === 0;
    }

    const hasActiveFilters = selectedBrands.length > 0
        || Object.values(filterLocal).some(values => values.length > 0)
        || (priceFilterLocal.min > priceFilter.min || priceFilterLocal.max < priceFilter.max);

    const resetFilters = () => {
        setSelectedBrands([]);
        setFilterLocal({});
        setPriceFilterLocal(priceFilter);
    }

    return (<div className="col-0 col-lg-4  col-xl-3 d-lg-block position-relative">
        <div className="filter">
            <div className="d-flex flex-column filter__content p-3 p-xl-4">
                <div className="d-lg-none filter__head p-3">
                    <div className="filter__t-big fw-medium">
                        Фильтр
                    </div>
                    <button type="button" className="btn-close filter__btn-close" aria-label="Закрыть"
                            onClick={toggleFilters}></button>
                </div>
                {priceFilter.max && <Range range={priceFilter} currentValue={priceFilterLocal.min > 0 || priceFilterLocal.max > 0 ? priceFilterLocal : undefined} name={'Цена'} setValue={setPriceFilterLocal} postfix={'р.'} />}
                <div className="d-flex flex-column filter__box pb-1 pb-xl-3 pt-1 pt-xl-3">
                    <div className="filter__item">
                        <div className="filter__t-min">
                            Бренд
                        </div>
                    </div>
                    {brands.map(brand => <div key={brand} className="filter__item">
                        <label className="filter__t-min form-check" >
                            <input className="form-check-input"  type="checkbox" checked={selectedBrands.includes(brand)}  onChange={() => {
                                selectedBrands.includes(brand)
                                    ? setSelectedBrands(selectedBrands.filter(sbrand => sbrand !== brand))
                                    : setSelectedBrands([...selectedBrands, brand])
                            }}/>
                            <div className="form-check-icon"></div>
                            <span className="form-check-text">
                             <span>{brand}</span>
                            </span>
                        </label>
                    </div>)}

                </div>
                {/*<div className="d-flex flex-column filter__box pb-1 pb-xl-3 pt-1 pt-xl-3">*/}
                {/*    <div className="filter__item">*/}
                {/*        <label className="filter__t-min form-check">*/}
                {/*            <input className="form-check-input" name="rating" type="checkbox"*/}
                {/*                   value="Высокий рейтинг"/>*/}
                {/*            <div className="form-check-icon"></div>*/}
                {/*            <span className="form-check-text">*/}
                {/*        <span className="fw-medium">Высокий рейтинг</span>*/}
                {/*        <span className="min">4 и 5 звезд</span>*/}
                {/*    </span>*/}
                {/*        </label>*/}
                {/*    </div>*/}
                {/*</div>*/}
                {/*<div className="d-flex flex-column filter__box pb-1 pb-xl-3 pt-1 pt-xl-3">*/}
                {/*    <div className="filter__item">*/}
                {/*        <label className="filter__t-min form-check">*/}
                {/*            <input className="form-check-input" name="reviews" type="checkbox"*/}
                {/*                   value="Только с отзывами"/>*/}
                {/*            <div className="form-check-icon"></div>*/}
                {/*            <span className="form-check-text">*/}
                {/*        <span>Только с отзывами</span>*/}
                {/*    </span>*/}
                {/*        </label>*/}
                {/*    </div>*/}
                {/*</div>*/}

                {filters
                    .filter(filter => filter.type.show_in_filters)
                    .sort((a, b) => {
                        // Sort by filter_order first (if available), then by name
                        const orderA = a.type.filter_order ?? Infinity;
                        const orderB = b.type.filter_order ?? Infinity;

                        if (orderA !== orderB) {
                            return orderA - orderB;
                        }

                        return a.type.name.localeCompare(b.type.name);
                    })
                    .map(filter => <div key={filter.type.id} className="d-flex flex-column filter__box pb-1 pb-xl-3 pt-1 pt-xl-3">
                        {showSortOrder && <div className={'text-end'}>Filter Order: {filter.type.filter_order}</div>}

                    {isAllValueNumeric(filter.possible_values) ?
                        <Range name={filter.type.name} range={{
                            min: Math.min(...filter.possible_values.map(v => parseInt(v)).filter(v => v > 0)),
                            max: Math.max(...filter.possible_values.map(v => parseInt(v)))
                        }}
                        currentValue={
                            filterLocal[filter.type.id] && filterLocal[filter.type.id].length > 0
                                ? ((): RangeFilterInterface => {
                                    const parts = filterLocal[filter.type.id][0].split(RANGE_DELIMITER);
                                    return {
                                        min: parseInt(parts[0]),
                                        max: parseInt(parts[1])
                                    };
                                })()
                                : undefined
                        }
                        setValue={(val) => setRangeFilter(filter.type.id, val)} />
                            :
                        <CheckboxFilterGroup
                            filter={filter}
                            selectedValues={filterLocal[filter.type.id] || []}
                            onToggle={(value) => toggleLocalFilter(filter.type.id, value)}
                        />

                }

                </div>)}

                <div className="p-3 p-xl-4 filter__btn-row sticky-bottom">
                    <button type="button" className="btn btn-primary big w-100 mb-2" onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        toggleFilters();
                    }}>Показать {productCount} товар{getSuffixByCount(productCount || 0)}</button>
                    {hasActiveFilters && (
                        <button type="button" className="btn btn-secondary big w-100 " onClick={resetFilters}>
                            Сбросить фильтры
                        </button>
                    )}
                </div>
                <span onClick={() => {setShowSortOrder(!showSortOrder)}}>.</span>
            </div>
        </div>
    </div>)
}